// iter158 Part 2 — Admin "Partner Activations" screen.
// Lists partner_grants rows (phone shown as last-4 only), supports a CSV
// download via the existing /partner/admin/partner-grants.csv endpoint,
// and toggles the kill switch via /partner/admin/partner-grant-switch.
// Admin-only; AuthGate wraps the route.

import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { FileSpreadsheet, RefreshCw, CheckCircle2, Ban } from "lucide-react";
import Navbar from "@/components/Navbar";
import { SsoToolkitPanel, HostSwitch } from "@/components/SsoToolkitPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPartnerGrants() {
  const [enabled, setEnabled] = useState(false);
  const [switches, setSwitches] = useState({ preview: false, live: false });
  const [currentHost, setCurrentHost] = useState("");
  const [grants, setGrants] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/partner/admin/partner-grants`);
      setEnabled(Boolean(r.data?.enabled));
      setSwitches(r.data?.switches || { preview: false, live: false });
      setCurrentHost(r.data?.currentHost || "");
      setGrants(r.data?.grants || []);
      setCount(r.data?.count || 0);
    } catch (err) {
      toast.error("Could not load partner activations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (host, next) => {
    setToggling(true);
    try {
      const r = await axios.post(`${API}/partner/admin/partner-grant-switch`, { enabled: next, host });
      setSwitches(r.data.switches);
      if (host === currentHost) setEnabled(next);
      toast.success(`Partner grant (${host}) ${next ? "ENABLED" : "disabled"}`);
    } catch (err) {
      toast.error("Could not toggle switch");
    } finally {
      setToggling(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const r = await axios.get(`${API}/partner/admin/partner-grants.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "partner-grants.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("CSV export failed"); }
  };

  return (
    <main className="min-h-screen bg-sand-50 pb-16" data-testid="admin-partner-grants-root">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 pt-6 sm:px-6 lg:px-8">
        <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-ink-900">Partner Activations</h1>
            <p className="mt-1 text-sm text-ink-700">
              Server-to-server grants from the Teacher Utility Toolkit "Combo Master" bundle.
              Full phone numbers are never exposed — only the last 4 digits.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={load} disabled={loading} data-testid="partner-reload-btn">
              <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Reload
            </Button>
            <Button variant="outline" onClick={downloadCsv} data-testid="partner-csv-btn">
              <FileSpreadsheet className="mr-1 h-4 w-4" /> CSV
            </Button>
          </div>
        </header>

        {/* Kill switch panel */}
        <section className={`mb-5 rounded-2xl border-2 p-4 ${enabled ? "border-moss/40 bg-moss/5" : "border-rose-300 bg-rose-50"}`} data-testid="partner-switch-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`rounded-full p-2 ${enabled ? "bg-moss/15 text-moss" : "bg-rose-100 text-rose-700"}`}>
                {enabled ? <CheckCircle2 className="h-5 w-5" /> : <Ban className="h-5 w-5" />}
              </div>
              <div>
                <p className="font-heading text-sm font-bold text-ink-900">
                  Partner grant API is {enabled ? "ENABLED" : "DISABLED"} on this host
                </p>
                <p className="text-xs text-ink-700">
                  {enabled
                    ? "The Toolkit server can activate PRO for teachers who buy the combo."
                    : "Grants return 403; revokes still work. Flip ON only when you're ready."}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <HostSwitch host="preview" label="Preview host" testPrefix="partner" checked={switches.preview} current={currentHost === "preview"} busy={toggling} onChange={toggle} />
            <HostSwitch host="live" label="LIVE host" testPrefix="partner" checked={switches.live} current={currentHost === "live"} busy={toggling} onChange={toggle} />
          </div>
        </section>

        <SsoToolkitPanel />

        <section className="rounded-xl border border-border bg-white p-4 shadow-sm" data-testid="partner-grants-table">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm text-ink-700">Showing <b>{count}</b> record{count === 1 ? "" : "s"}.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-sand-100 text-left text-ink-700">
                <tr>
                  <th className="p-2 text-xs font-semibold uppercase tracking-wider">Granted At</th>
                  <th className="p-2 text-xs font-semibold uppercase tracking-wider">Phone (last 4)</th>
                  <th className="p-2 text-xs font-semibold uppercase tracking-wider">Name</th>
                  <th className="p-2 text-xs font-semibold uppercase tracking-wider">Days</th>
                  <th className="p-2 text-xs font-semibold uppercase tracking-wider">Order Id</th>
                  <th className="p-2 text-xs font-semibold uppercase tracking-wider">Status</th>
                  <th className="p-2 text-xs font-semibold uppercase tracking-wider">Expiry</th>
                  <th className="p-2 text-xs font-semibold uppercase tracking-wider">Created?</th>
                </tr>
              </thead>
              <tbody>
                {grants.map((g) => (
                  <tr key={g.id} className="border-t border-border hover:bg-sand-50" data-testid={`partner-row-${g.orderId}`}>
                    <td className="p-2 font-mono text-xs">{(g.grantedAt || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="p-2 font-mono text-xs">••{g.phoneLast4}</td>
                    <td className="p-2 text-xs">{g.name || "—"}</td>
                    <td className="p-2 font-mono text-xs">{g.days}</td>
                    <td className="p-2 font-mono text-[0.7rem]">{g.orderId}</td>
                    <td className="p-2">
                      <span className={`rounded-full px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider ${g.status === "active" ? "bg-moss/15 text-moss-dark" : "bg-rose-100 text-rose-700"}`}>
                        {g.status}
                      </span>
                    </td>
                    <td className="p-2 font-mono text-xs">{(g.expiryDate || "").slice(0, 10) || "—"}</td>
                    <td className="p-2 text-xs">{g.created ? "yes" : "—"}</td>
                  </tr>
                ))}
                {grants.length === 0 && !loading && (
                  <tr><td colSpan={8} className="p-6 text-center text-sm text-ink-700" data-testid="partner-empty">No partner activations yet.</td></tr>
                )}
                {loading && (
                  <tr><td colSpan={8} className="p-6 text-center text-sm text-ink-700">Loading…</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </main>
  );
}
